import {
  Box,
  ThemeProvider,
  Typography,
} from "@mui/material";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import React, { useEffect, useRef, useState } from "react";
import { commonTableStyles as styles, TABLE_TOKENS } from "../styles";
import { CarCompany } from "utils/modals";
import MUIDataTable from "mui-datatables";
import { getTableTheme } from "../tableTheme";
import DeleteEditModal from "components/customModal/DeleteEditModal";
import ContainedButton from "components/button/contained-button";
import "../style.css";

interface Props {
  onEditPress: Function;
  onDeletePress: Function;
  onAddPress?: () => void;
  currentPage: number;
  totalRecords: number;
  rowsPerPage: number;
  handleChangePage: Function;
  carCompaniesList: Array<CarCompany>;
  handleChangeRowsPerPage: Function;
  searchHandler: any;
}

const SEARCH_DEBOUNCE_MS = 500;

const VehicleTable = (props: Props) => {
  const { carCompaniesList, searchHandler, onAddPress } = props;

  const [modalOpen, setModalOpen] = useState(false);
  const [deleteData] = useState<any>({});
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSearchRef = useRef("");

  const clearPendingSearch = () => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
  };

  useEffect(() => clearPendingSearch, []);

  const handleSearchChange = (text: string | null) => {
    const nextText = `${text || ""}`.trim();
    clearPendingSearch();
    if (nextText === lastSearchRef.current) return;
    searchTimerRef.current = setTimeout(() => {
      lastSearchRef.current = nextText;
      searchHandler(nextText);
    }, SEARCH_DEBOUNCE_MS);
  };

  const tableData = React.useMemo(
    () =>
      (carCompaniesList || []).map((row: any) => ({
        ...row,
        name: row?.name || row?.vehicleName || "",
        brandName:
          row?.brandName || row?.companyName || row?.carCompany?.name || "",
        vehicleTypeLabel:
          row?.vehicleTypeLabel ||
          (String(row?.vehicleType) === "1" ? "Bike" : "Car"),
      })),
    [carCompaniesList]
  );

  const columns: any = [
    {
      name: "id",
      label: "S. No.",
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (_dataIndex: number, rowIndex: number) => (
          <div>
            {(props.currentPage - 1) * (props.rowsPerPage || 10) + rowIndex + 1}
          </div>
        ),
      },
    },
    {
      name: "name",
      label: "Vehicle",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any) => <div>{value || "N/A"}</div>,
      },
    },
    {
      name: "brandName",
      label: "Company",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any) => <div>{value || "N/A"}</div>,
      },
    },
    {
      name: "vehicleTypeLabel",
      label: "Vehicle Type",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any) => <div>{value || "N/A"}</div>,
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRenderLite: (dataIndex: number) => {
          const row = tableData[dataIndex];
          return (
            <div className="icon-container icon-container-2">
              <BorderColorIcon
                style={{ color: TABLE_TOKENS.accent, fontSize: "1.3rem" }}
                onClick={() => {
                  props.onEditPress(row);
                }}
              />
            </div>
          );
        },
      },
    },
  ];

  const options: any = {
    print: false,
    filter: false,
    download: false,
    serverSide: true,
    viewColumns: false,
    selectableRows: "none",
    search: true,
    onSearchChange: (text: string | null) => handleSearchChange(text),
    customToolbar: () =>
      onAddPress ? (
        <ContainedButton
          externalStyles={styles.toolbarAddBtn}
          onClick={onAddPress}
          title="Add vehicle"
        />
      ) : null,
    count: Math.max(props.totalRecords || 0, carCompaniesList?.length || 0),
    page: props.currentPage - 1,
    rowsPerPage: props.rowsPerPage || 10,
    onChangePage: (currentPage: number) => props.handleChangePage(currentPage),
    onChangeRowsPerPage: (numberOfRows: number) =>
      props.handleChangeRowsPerPage(numberOfRows),
    rowsPerPageOptions: [10, 20, 50, 100],
    searchPlaceholder: " Enter Vehicle Name .",
  };
  const TableComponent: any = MUIDataTable;
  const renderView = () => {
    return (
      <Box sx={styles.paper}>
        <ThemeProvider theme={getTableTheme()}>
          <TableComponent
            title={
              <Typography variant="h5" sx={styles.tableHeadingTxt}>
                {`Vehicles List`}
              </Typography>
            }
            data={tableData}
            columns={columns}
            options={options}
          />
        </ThemeProvider>
        <DeleteEditModal
          onConfirm={() => {
            props.onDeletePress(deleteData);
            setModalOpen(false);
          }}
          visible={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Delete Confirmation"
          children={"Do You Really Want to Delete this records ? "}
        />
      </Box>
    );
  };

  return <Box>{renderView()}</Box>;
};

export default VehicleTable;
